import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function AuthAction() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const [status, setStatus] = useState('loading'); // loading, success, error, input
  const [message, setMessage] = useState('جاري معالجة طلبك...');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (!mode || !oobCode) {
      setStatus('error');
      setMessage('رابط غير صالح أو منتهي الصلاحية');
      return;
    }

    const handleAction = async () => {
      try {
        if (mode === 'verifyEmail') {
          await applyActionCode(auth, oobCode);
          setStatus('success');
          setMessage('تم تأكيد بريدك الإلكتروني بنجاح!');
          // Redirect to auth handler or dashboard after a short delay
          setTimeout(() => navigate('/dashboard'), 3000);
        } else if (mode === 'resetPassword') {
          // Verify code first
          await verifyPasswordResetCode(auth, oobCode);
          setStatus('input'); // Show form to enter new password
          setMessage('الرجاء إدخال كلمة المرور الجديدة.');
        } else {
          setStatus('error');
          setMessage('إجراء غير معروف');
        }
      } catch (error) {
        console.error(error);
        setStatus('error');
        setMessage(error.message || 'حدث خطأ أثناء معالجة طلبك');
      }
    };

    handleAction();
  }, [mode, oobCode, navigate]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('جاري تحديث كلمة المرور...');
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus('success');
      setMessage('تم تحديث كلمة المرور بنجاح! يمكنك الآن تسجيل الدخول.');
    } catch (error) {
      console.error(error);
      setStatus('error');
      setMessage(error.message || 'حدث خطأ أثناء تحديث كلمة المرور');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-zinc-950 p-4 font-sans text-right" dir="rtl">
      <Link to="/" className="mb-8 block transition-transform duration-300 hover:scale-105">
        <img src="/lobab.png" alt="Lobab School" className="h-14 md:h-16 w-auto object-contain drop-shadow-sm" />
      </Link>
      <div className="bg-white dark:bg-zinc-900 p-8 rounded-2xl shadow-xl max-w-md w-full border border-slate-100 dark:border-zinc-800 text-center">
        {status === 'loading' && (
          <div className="flex flex-col items-center">
            <Loader2 className="w-12 h-12 text-primary-500 animate-spin mb-4" />
            <p className="text-slate-600 dark:text-slate-400">{message}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">نجاح</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{message}</p>
            <Link to="/login" className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-2 rounded-lg font-medium transition-colors">
              الذهاب لتسجيل الدخول
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center text-center">
            <XCircle className="w-12 h-12 text-rose-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">خطأ</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{message}</p>
            <Link to="/login" className="bg-slate-200 dark:bg-zinc-800 hover:bg-slate-300 dark:hover:bg-zinc-700 text-slate-900 dark:text-white px-6 py-2 rounded-lg font-medium transition-colors">
              العودة
            </Link>
          </div>
        )}

        {status === 'input' && (
          <div className="flex flex-col text-right">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">إعادة تعيين كلمة المرور</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{message}</p>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  كلمة المرور الجديدة
                </label>
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  dir="ltr"
                  className="w-full px-4 py-3 border border-slate-300 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
                  minLength={6}
                />
              </div>
              <button
                type="submit"
                className="w-full bg-primary-500 hover:bg-primary-600 text-white px-4 py-3 rounded-lg font-medium transition-colors"
              >
                تغيير كلمة المرور
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}